import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import { Form, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button, Field, FormAlerts } from '@cordata/buffet';
import * as Yup from 'yup';

import {
  createStudyFlag,
  updateStudyFlag,
} from '~/utils/situation-tables/study-flags-api';
import useSlice from '~/utils/slice/hook';

import OptionsField from './OptionsField';

const VALUE_TYPE_OPTIONS = [
  { value: 'boolean', label: 'True or False' },
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'select', label: 'Single Select' },
  { value: 'date', label: 'Date' },
];

const formikEnhancer = withFormik({
  displayName: 'StudyFlagFormikEnhancer',
  validateOnChange: false,
  validateOnBlur: false,

  mapPropsToValues: (props) => {
    if (props.data) {
      const valueTypeOption = VALUE_TYPE_OPTIONS.find(opt => opt.value === props.data.value_type);

      return {
        id: props.data.id,
        question_text: props.data.question_text || '',
        value_type: valueTypeOption || '',
        org_uid: props.data.org_uid || '',
        division_uid: props.data.division_uid || '',
        options: props.data.study_flag_options || [],
      };
    }

    return {
      question_text: '',
      value_type: '',
      org_uid: '',
      division_uid: '',
      options: [],
    };
  },

  validationSchema: Yup.object().shape({
    question_text: Yup.string()
      .required('Question is required')
      .max(500, 'Question must be 500 characters or less')
      .trim(),
    value_type: Yup.string().required('Value type is required'),
    options: Yup.array().when('value_type', {
      is: (value) => value === 'select',
      then: Yup.array()
        .of(Yup.object().shape({
          option_value: Yup.string()
            .required('Option value is required')
            .max(255, 'Option value must be 255 characters or less')
            .trim(),
        }))
        .min(1, 'At least one option required for select type')
        .max(100, 'Maximum 100 options allowed'),
    }),
  }),

  handleSubmit: (values, { ...bag }) => {
    const { props: { onSuccess } } = bag;

    const valueType = typeof values.value_type === 'object' ? values.value_type.value : values.value_type;

    const payload = {
      question_text: values.question_text,
      value_type: valueType,
    };

    payload.org_uid = values.org_uid?.value || '';
    payload.division_uid = values.division_uid?.value || '';

    if ((valueType === 'select') && values.options) {
      payload.options = values.options.map((opt, index) => ({
        option_value: opt.option_value,
        sort_order: opt.sort_order !== undefined ? opt.sort_order : index,
      }));
    }

    bag.setSubmitting(true);

    if (values.id) { // edit
      updateStudyFlag(values.id, payload)
        .then(() => {
          if (onSuccess) onSuccess();
        })
        .catch(e => {
          console.error(e);
          bag.setErrors({ server: e });
        })
        .finally(() => bag.setSubmitting(false));
    } else { // new
      createStudyFlag(payload)
        .then(() => {
          if (onSuccess) onSuccess();
        })
        .catch(e => {
          console.error(e);
          bag.setErrors({ server: e });
        })
        .finally(() => bag.setSubmitting(false));
    }
  },
});

const StudyFlagForm = ({
  errors,
  handleSubmit,
  isSubmitting,
  submitCount,
  values,
  setFieldValue,
  onCancel,
  isOpen,
}) => {
  const { fetch } = useSlice();
  const [orgOptions, setOrgOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [showOptions, setShowOptions] = useState(values.value_type === 'select');

  useEffect(() => {
    fetch('organization').then(({ data: { data } }) => {
      setOrgOptions(data.map(org => ({ value: org.id, label: org.label })));
    });
  }, []);

  useEffect(() => {
    if (values.org_uid) {
      const orgUid = typeof values.org_uid === 'object' ? values.org_uid.value : values.org_uid;
      const divisionUid = typeof values.division_uid === 'object' ? values.division_uid.value : values.division_uid;

      fetch('division', { 'org-uid': orgUid }).then(({ data: { data } }) => {
        const divOptions = data.map(div => ({ value: div.id, label: div.label }));
        setDivisionOptions(divOptions);
        // if the current set division_uid is not found in the options, clear the field
        if (!divOptions.find((div) => div.value === divisionUid)) {
          setFieldValue('division_uid', '');
        }
      });
    } else {
      setDivisionOptions([]);
      if (values.division_uid) {
        setFieldValue('division_uid', '');
      }
    }
  }, [values.org_uid]);

  useEffect(() => {
    setShowOptions(values.value_type?.value === 'select');
  }, [values.value_type]);

  return (
    <Modal isOpen={isOpen} backdrop="static" size="xl">
      <Form onSubmit={handleSubmit} noValidate>
        <ModalHeader>{values.id ? 'Edit' : 'Add'} Study Flag</ModalHeader>

        <ModalBody>
          { submitCount >= 1 && errors && <FormAlerts errors={errors} /> }

          <Field
            name="question_text"
            label="Question"
            required
          />

          <Field
            name="value_type"
            label="Value Type"
            type="select"
            options={VALUE_TYPE_OPTIONS}
            required
          />

          <Field
            name="org_uid"
            label="Organization (optional)"
            type="select"
            options={orgOptions}
          />

          <Field
            name="division_uid"
            label="Division (optional)"
            type="select"
            options={divisionOptions}
            disabled={!values.org_uid}
          />

          {(showOptions) && (
            <OptionsField values={values} />
          )}
        </ModalBody>

        <ModalFooter className="d-flex">
          <div className="ml-auto">
            <Button
              type="button"
              outline
              disabled={isSubmitting}
              onClick={onCancel}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Working ...' : 'Save'}
            </Button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

StudyFlagForm.propTypes = {
  errors: PropTypes.shape(),
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  submitCount: PropTypes.number.isRequired,
  values: PropTypes.shape({
    id: PropTypes.string,
    question_text: PropTypes.string,
    value_type: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ]),
    org_uid: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ]),
    division_uid: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ]),
    options: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};

StudyFlagForm.defaultProps = {
  errors: {},
};

export default formikEnhancer(StudyFlagForm);
